import * as R from 'ramda';

import {
  ARDOTAttribute,
  AssetRequestAttribute,
  AssetRequestCreateInput,
  AssetRequestsFilters,
  AssetRequestStatus,
  AssetRequestType,
  Column,
  ConfirmModalContent,
} from '@atom/types/assetRequest';
import {
  AssetDetail,
  InventoryAssetDetailType,
  InventorySchemaItem,
} from '@atom/types/inventory';
import { SchemasInput } from '@atom/types/schema';
import {
  ASSET_REQUEST_EMULATION_CLIENT,
  Client,
  getCurrentClient,
} from '@atom/utilities/featureToggleUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import {
  ARDOT_CONFIRM_MODAL_CONTENT,
  ardotColumns,
  INVENTORY_STATUS_OPTIONS,
  REPLENISHMENT_STATUS_OPTIONS,
  SDDOT_CONFIRM_MODAL_CONTENT,
  SDDOT_FUEL_SCHEMA_ID,
  SDDOT_INVENTORY_SCHEMA_ID,
  SDDOT_STOCKPILE_SCHEMA_ID,
  sddotColumns,
  TYPE_LABELS,
} from './assetRequestConstants';

interface CustomValues {
  initialFilters: AssetRequestsFilters;
  typeOptions: AssetRequestType[];
}

// INITIAL FILTERS
const getSDDOTInitialFilters = (
  schema?: InventorySchemaItem,
  asset?: InventoryAssetDetailType,
): AssetRequestsFilters => {
  if (isNilOrEmpty(schema) && isNilOrEmpty(asset)) {
    return {
      type: AssetRequestType.SDDOT_INVENTORY_TRANSFER,
      schemaIds: [SDDOT_INVENTORY_SCHEMA_ID],
    };
  }

  const type =
    schema?.id === SDDOT_STOCKPILE_SCHEMA_ID
      ? AssetRequestType.SDDOT_STOCKPILE_TRANSFER
      : AssetRequestType.SDDOT_INVENTORY_TRANSFER;

  const addAssetFilters =
    type === AssetRequestType.SDDOT_INVENTORY_TRANSFER ||
    type === AssetRequestType.SDDOT_STOCKPILE_TRANSFER;

  return {
    type,
    assetId: asset?.id,
    ...(addAssetFilters && {
      fromAssetId: asset?.id,
    }),
  };
};

const getARDOTInitialFilters = (
  schema?: InventorySchemaItem,
  asset?: InventoryAssetDetailType,
): AssetRequestsFilters => {
  if (isNilOrEmpty(schema) && isNilOrEmpty(asset)) {
    return {
      type: AssetRequestType.ARDOT_118_TRANSFER,
    };
  }

  return {
    type: AssetRequestType.ARDOT_118_TRANSFER,
    assetId: asset?.id,
  };
};

// TYPE OPTIONS
const getSDDOTTypeOptions = (
  schema: InventorySchemaItem,
): AssetRequestType[] => {
  if (isNilOrEmpty(schema)) {
    return [
      AssetRequestType.SDDOT_INVENTORY_TRANSFER,
      AssetRequestType.SDDOT_STOCKPILE_TRANSFER,
      AssetRequestType.SDDOT_REPLENISHMENT,
      AssetRequestType.SDDOT_ADJUSTMENT,
    ];
  }

  return [
    ...[
      schema?.id === SDDOT_STOCKPILE_SCHEMA_ID
        ? AssetRequestType.SDDOT_STOCKPILE_TRANSFER
        : AssetRequestType.SDDOT_INVENTORY_TRANSFER,
    ],
    AssetRequestType.SDDOT_REPLENISHMENT,
    AssetRequestType.SDDOT_ADJUSTMENT,
  ];
};

// STATUS OPTIONS
export const getSDDOTStatusOptions = (
  type: AssetRequestType,
): AssetRequestStatus[] => {
  const statusOptions = {
    [AssetRequestType.SDDOT_REPLENISHMENT]: REPLENISHMENT_STATUS_OPTIONS,
    [AssetRequestType.SDDOT_INVENTORY_TRANSFER]: INVENTORY_STATUS_OPTIONS,
  };

  return statusOptions[type] || [];
};

// FULL CUSTOM VALUES
export const getCustomValues = (
  schema?: InventorySchemaItem,
  asset?: InventoryAssetDetailType,
): CustomValues => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return {
        initialFilters: getSDDOTInitialFilters(schema, asset),
        typeOptions: getSDDOTTypeOptions(schema),
      };
    case Client.ARDOT:
      return {
        initialFilters: getARDOTInitialFilters(schema, asset),
        typeOptions: [],
      };
    default:
      return {
        initialFilters: {},
        typeOptions: [],
      };
  }
};

export const getSchemaFilterInput = (): Partial<SchemasInput> => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return {
        ids: [
          SDDOT_FUEL_SCHEMA_ID,
          SDDOT_INVENTORY_SCHEMA_ID,
          SDDOT_STOCKPILE_SCHEMA_ID,
        ],
      };
    case Client.ARDOT:
      return { isMaterial: 'true' };
    default:
      return {};
  }
};

// This method will adjust filters before they are applied with tenant specific logic
export const cleanFilters = (
  filters: AssetRequestsFilters,
): AssetRequestsFilters => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.ARDOT:
    case Client.SDDOT:
      return {
        ...R.reject(isNilOrEmpty, {
          ...filters,
        }),
        // Name filter only allows user to enter the sequence suffix, so we must
        // hydrate the type prefix here.
        ...(filters.name && {
          name: `${TYPE_LABELS[filters.type]} - ${filters.name}`,
        }),
      };
    default:
      return filters;
  }
};

// Gets filters for when the request type is changed
export const getSDDOTTypeChangeFilters = (
  type: AssetRequestType,
  asset?: InventoryAssetDetailType,
): AssetRequestsFilters => {
  if (isNilOrEmpty(asset)) {
    return { type };
  }

  const updateAssetFilters =
    type === AssetRequestType.SDDOT_INVENTORY_TRANSFER ||
    type === AssetRequestType.SDDOT_STOCKPILE_TRANSFER;

  return {
    type,
    assetId: asset?.id,
    ...(updateAssetFilters && {
      fromAssetId: asset?.id,
    }),
  };
};

export const getSDDOTColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
): Column[] => {
  return sddotColumns(type, isPortalView);
};

export const getARDOTColumns = (isPortalView: boolean): Column[] => {
  return ardotColumns(isPortalView);
};

export const getTableColumns = (
  type: AssetRequestType,
  isPortalView: boolean,
) => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return getSDDOTColumns(type, isPortalView);
    case Client.ARDOT:
      return getARDOTColumns(isPortalView);
    default:
      return [];
  }
};

const defaultModalContent = { title: '', content: '' };

export const getSDDOTConfirmModalContent = (
  type: AssetRequestType,
): ConfirmModalContent => {
  return SDDOT_CONFIRM_MODAL_CONTENT[type] || defaultModalContent;
};

export const getConfirmModalContent = (
  type: AssetRequestType,
): ConfirmModalContent => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return getSDDOTConfirmModalContent(type);
    case Client.ARDOT:
      return ARDOT_CONFIRM_MODAL_CONTENT;
    default:
      return defaultModalContent;
  }
};

export const getSDDOTReplenishmentSubtextOptions = (
  totalOrdered: number,
  totalReceived: number = 0,
  newReceived: number,
  assetUnit: string,
): { icon: string; message: string } => {
  if (R.isNil(newReceived) || R.isNil(totalOrdered)) {
    return null;
  }

  const totalRemaining = totalOrdered - totalReceived - newReceived;

  switch (true) {
    case totalRemaining > 0:
      return {
        icon: 'warning',
        message: `Total remaining quantity on order: ${totalRemaining} ${assetUnit}. You can add more replenishments later.`,
      };
    case totalRemaining < 0:
      return {
        icon: 'warning',
        message:
          'You received more than you ordered. This is OK - no further action is required.',
      };
    case totalRemaining === 0:
      return {
        icon: 'check_circle',
        message: 'You have received the full quantity.',
      };
    default:
      return null;
  }
};

export const getAttributeFilterByName = (
  asset: InventoryAssetDetailType,
  attributeName: string,
) => {
  if (isNilOrEmpty(asset)) {
    return null;
  }

  const attributeId = R.keys(asset?.attributes).filter(id => {
    return asset?.attributes[id]?.name === attributeName;
  })[0];

  return {
    [`attributes.${attributeId}`]: [asset?.attributes[attributeId]?.value],
  };
};

export const getApprovalKeyword = (status: AssetRequestStatus): string => {
  const keywords = {
    [AssetRequestStatus.CANCELED]: 'Cancel',
    [AssetRequestStatus.REJECTED]: 'Reject',
    [AssetRequestStatus.FULL_APPROVAL]: 'Approve',
    [AssetRequestStatus.PARTIAL_APPROVAL]: 'Approve',
    [AssetRequestStatus.PENDING_APPROVAL]: 'Approve',
    [AssetRequestStatus.APPROVED]: 'Approve',
  };

  return keywords[status] || '';
};

const ARDOTAttributes = [
  ARDOTAttribute.TAG_NUMBER,
  ARDOTAttribute.DESCRIPTION,
  ARDOTAttribute.OPERATOR_NAME,
  ARDOTAttribute.COMMUTE_STATUS,
  ARDOTAttribute.MONITORED_STATUS,
];

export const ardotAttributeDictionary = (asset: InventoryAssetDetailType) => {
  const attributes = R.pathOr({}, ['attributes'], asset);

  return R.keys(attributes).reduce((acc, attributeId) => {
    const attribute = { id: attributeId, ...asset?.attributes[attributeId] };

    return ARDOTAttributes.includes(attribute?.name as ARDOTAttribute)
      ? { ...acc, [attribute?.name]: attribute }
      : acc;
  }, {});
};

export const ardotAttributeDictionaryAssetDetail = (asset: AssetDetail) => {
  const attributes = asset?.attributeGroups.reduce((acc, group) => {
    return [...acc, ...group.attributes];
  }, []);

  return attributes.reduce((acc, attribute) => {
    return ARDOTAttributes.includes(attribute?.name as ARDOTAttribute)
      ? { ...acc, [attribute?.name]: attribute }
      : acc;
  }, {});
};

export const getARDOTInitialAttributes = (
  asset: InventoryAssetDetailType,
): AssetRequestAttribute[] => {
  const attributesDictionary = ardotAttributeDictionary(asset);
  const ARDOTCreateAttributes = [
    ARDOTAttribute.OPERATOR_NAME,
    ARDOTAttribute.COMMUTE_STATUS,
    ARDOTAttribute.MONITORED_STATUS,
  ];

  return ARDOTCreateAttributes.map((attributeName: ARDOTAttribute) => {
    const attribute = attributesDictionary[attributeName];

    return {
      id: attribute?.id,
      name: attribute?.name,
      oldValue: attribute?.value,
      newValue: attribute?.value,
    };
  });
};

export const getCategoryPathString = (
  asset: InventoryAssetDetailType,
): string => {
  const pathValues = R.pathOr([], ['categories'], asset).map(
    category => category.name,
  );
  return pathValues.join(' / ');
};

export const getInitialCreateInputs = (
  type: AssetRequestType,
  asset: InventoryAssetDetailType,
): AssetRequestCreateInput => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return {
        type,
        assetId: asset?.id,
      };
    case Client.ARDOT:
      return {
        type,
        assetId: asset?.id,
        categoryId: asset?.categoryId,
        categoryPath: getCategoryPathString(asset),
        attributes: getARDOTInitialAttributes(asset),
      };
    default:
      return { type: null, assetId: '' };
  }
};

export const cleanCreateInputs = (
  input: AssetRequestCreateInput,
): AssetRequestCreateInput => {
  const attributes = R.pathOr([], ['attributes'], input);

  return {
    ...input,
    attributes: attributes?.filter(
      attribute => attribute?.newValue !== attribute?.oldValue,
    ),
  };
};

export const getConfirmationTitle = (approvalKeyword: string): string => {
  const currentClient = getCurrentClient(ASSET_REQUEST_EMULATION_CLIENT);

  switch (currentClient) {
    case Client.SDDOT:
      return `${approvalKeyword} Request: Inventory Transfer?`;
    case Client.ARDOT:
      return `${approvalKeyword} 118 Process Request?`;
    default:
      return '';
  }
};
